import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Grid,
  Chip,
  LinearProgress,
  useTheme,
  alpha,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  LocalFireDepartment as IgnitionIcon,
  Foundation as FoundationIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { AssessmentCard } from './AssessmentCard';
import { ProgrammeAssessmentSectionProps, AssessmentCategory } from './types';

export const ProgrammeAssessmentSection: React.FC<ProgrammeAssessmentSectionProps> = ({
  programmeType,
  clientId,
  assessmentData,
  onAssessmentClick,
  isExpanded,
  onToggleExpand,
}) => {
  const theme = useTheme();

  const getAssessmentCategories = (stage: 'Initial' | 'Review'): AssessmentCategory[] => {
    if (programmeType === 'Foundation') {
      if (stage === 'Initial') {
        return ['Inbody', 'Movement', 'Allied Health', 'Nutrition', 'Training'];
      } else {
        // Foundation Review doesn't include Allied Health
        return ['Inbody', 'Movement', 'Nutrition', 'Training'];
      }
    }
    return ['Inbody', 'Movement', 'Metabolic', 'Nutrition', 'Training'];
  };

  const getProgrammeIcon = () => {
    return programmeType === 'Foundation' ? <FoundationIcon /> : <IgnitionIcon />;
  };

  const getProgrammeColor = () => {
    return programmeType === 'Foundation' ? theme.palette.info.main : theme.palette.error.main;
  };

  const checkAssessmentSubmitted = (category: AssessmentCategory, stage: 'Initial' | 'Review') => {
    return assessmentData.some(data => {
      // Check if the uploadCategory contains the category (case-insensitive)
      const categoryMatch = data.uploadCategory.toLowerCase().includes(category.toLowerCase());
      
      // Check if the assessment stage matches
      const stageMatch = data.assessmentStage === stage;
      
      // Check if the programme type matches
      const programmeMatch = data.programmeType === programmeType;
      
      return categoryMatch && stageMatch && programmeMatch;
    });
  };

  const calculateCompletionStats = () => {
    const initialCategories = getAssessmentCategories('Initial');
    const reviewCategories = getAssessmentCategories('Review');
    
    const initialCount = initialCategories.filter(cat => checkAssessmentSubmitted(cat, 'Initial')).length;
    const reviewCount = reviewCategories.filter(cat => checkAssessmentSubmitted(cat, 'Review')).length;
    const totalPossible = initialCategories.length + reviewCategories.length;

    return {
      initial: initialCount,
      review: reviewCount,
      total: initialCount + reviewCount,
      percentage: ((initialCount + reviewCount) / totalPossible) * 100,
      initialTotal: initialCategories.length,
      reviewTotal: reviewCategories.length,
    };
  };

  const stats = calculateCompletionStats();
  const programmeColor = getProgrammeColor();

  const containerVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: {
      opacity: 1,
      height: 'auto',
      transition: {
        height: {
          duration: 0.3,
        },
        opacity: {
          duration: 0.2,
        },
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      height: 0,
      transition: {
        height: {
          duration: 0.3,
        },
        opacity: {
          duration: 0.2,
        },
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 100,
        damping: 15,
      },
    },
  };

  return (
    <Accordion
      expanded={isExpanded}
      onChange={onToggleExpand}
      sx={{
        mb: 2,
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(programmeColor, 0.2)}`,
        '&:before': {
          display: 'none',
        },
        '&.Mui-expanded': {
          margin: '0 0 16px 0',
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ExpandMoreIcon />
          </motion.div>
        }
        sx={{
          background: `linear-gradient(135deg, ${alpha(programmeColor, 0.08)} 0%, ${alpha(programmeColor, 0.03)} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${alpha(programmeColor, 0.12)} 0%, ${alpha(programmeColor, 0.05)} 100%)`,
          },
        }}
      >
        <Box sx={{ width: '100%', pr: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  color: programmeColor,
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: alpha(programmeColor, 0.1),
                }}
              >
                {getProgrammeIcon()}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {programmeType} Programme
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Initial & Review Assessments
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={2} alignItems="center">
              <Box textAlign="right">
                <Typography variant="h5" fontWeight={700} color={programmeColor}>
                  {stats.percentage.toFixed(0)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Complete
                </Typography>
              </Box>
              <Stack spacing={0.5}>
                <Chip
                  label={`Initial: ${stats.initial}/${stats.initialTotal}`}
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={`Review: ${stats.review}/${stats.reviewTotal}`}
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                    fontWeight: 600,
                  }}
                />
              </Stack>
            </Stack>
          </Stack>

          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={stats.percentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: alpha(programmeColor, 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: `linear-gradient(90deg, ${programmeColor} 0%, ${alpha(programmeColor, 0.7)} 100%)`,
                },
              }}
            />
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 3 }}>
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Initial Assessment Reports */}
              <motion.div variants={itemVariants}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                  Initial Assessment Reports
                </Typography>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {getAssessmentCategories('Initial').map((category) => {
                    const stage: 'Initial' = 'Initial';
                    const isSubmitted = checkAssessmentSubmitted(category, stage);
                    const isClickable =
                      category === 'Allied Health' &&
                      programmeType === 'Foundation' &&
                      !!onAssessmentClick &&
                      isSubmitted;

                    return (
                      <Grid item xs={12} sm={6} md={4} key={`initial-${category}`}>
                        <AssessmentCard
                          title={category}
                          isSubmitted={isSubmitted}
                          onClick={isClickable ? () => onAssessmentClick(category, stage) : undefined}
                          isClickable={isClickable}
                          badgeLabel={isClickable ? 'View details' : undefined}
                          category={category}
                          programmeType={programmeType}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </motion.div>

              {/* Review Assessment Reports */}
              <motion.div variants={itemVariants}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                  Review Assessment Reports
                </Typography>
                <Grid container spacing={2}>
                  {getAssessmentCategories('Review').map((category) => (
                    <Grid item xs={12} sm={6} md={4} key={`review-${category}`}>
                      <AssessmentCard
                        title={category}
                        isSubmitted={checkAssessmentSubmitted(category, 'Review')}
                        onClick={() => onAssessmentClick?.(category, 'Review')}
                        isClickable={false}
                        category={category}
                        programmeType={programmeType}
                      />
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </AccordionDetails>
    </Accordion>
  );
};
